import { Formik } from 'formik';
import * as yup from 'yup';

import { BrandSettingsForm } from '../BrandSettingsForm/BrandSettingsForm';
import { useBrandedEmailsSettings } from '../../hooks/useBrandedEmailsSettings';
import { useSubscriptionFeature } from '@common/hooks';
import { BrandedEmailsSettingsRepository, BrandedSettingsSaveParams, ClientFeature } from '@packlink/packlink-sdk';
import { useToast } from '@shipengine/giger';
import { TFunction, useTranslation } from '@packlink/translation-provider';
import {
    NotificationBody,
    SubscriptionNotification,
} from '@common/components/SubscriptionNotification/SubscriptionNotification';
import { AmplitudeEventPrefixes, AmplitudeEvents } from '@constants/amplitude';
import { useSdk } from '@packlink/packlink-sdk-provider';

export type FormFields = Partial<BrandedSettingsSaveParams>;
const MAX_BYTE_FILE_SIZE = 200000; // 200Kb
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

export function BrandSettings(): JSX.Element {
    const { t } = useTranslation();
    const toast = useToast(t);
    const validationSchema = getValidationSchema(t);
    const { apiClient } = useSdk();

    const {
        isTenantSubscriptionEnabled,
        feature,
        isLoading: isSubscriptionLoading,
    } = useSubscriptionFeature(ClientFeature.BRANDED_EMAILS);
    const isSubscriptionFeatureNotEnabled = isTenantSubscriptionEnabled && !feature.isEnabled && !isSubscriptionLoading;
    const { brandedEmails, isLoading } = useBrandedEmailsSettings(feature.isEnabled);
    const initialValues = !isSubscriptionFeatureNotEnabled ? brandedEmails?.brandedSettings : undefined;

    const handleSuccess = () => {
        toast.success({
            title: t('brand-settings.form.submit-success-title'),
            message: t('brand-settings.form.submit-success'),
        });
    };

    const onSubmit = (values: FormFields) => {
        const brandedEmailsSettingsRepository = new BrandedEmailsSettingsRepository(apiClient);

        const params = getInputParams(values);
        const isNewSettings = !initialValues;

        return brandedEmailsSettingsRepository
            .saveSettings(params, isNewSettings)
            .then(() => {
                handleSuccess?.();
            })
            .catch(() => {
                toast.error({
                    message: t('branded-emails.error.try-again'),
                });
            });
    };
    return (
        <>
            {!isSubscriptionLoading && !isLoading && (
                <Formik
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    initialValues={{
                        companyName: initialValues?.companyName,
                        companyReplyToEmail: initialValues?.companyReplyToEmail,
                        companyLogo: initialValues?.companyLogoUrl,
                    }}
                    validateOnBlur
                    validateOnChange
                >
                    <div>
                        {isSubscriptionFeatureNotEnabled && (
                            <SubscriptionNotification>
                                <NotificationBody
                                    title={t('subscriptions.upgrade-notification.branded-emails')}
                                    amplitudeEvent={`${AmplitudeEvents.SETTINGS_TOOLTIP_BRANDED_EMAIL} ${AmplitudeEventPrefixes.CONFIGURATION_MENU}`}
                                >
                                    {t('subscriptions.upgrade-notification.branded-emails-message')}
                                </NotificationBody>
                            </SubscriptionNotification>
                        )}
                        <BrandSettingsForm isFormDisabled={isSubscriptionFeatureNotEnabled} />
                    </div>
                </Formik>
            )}
        </>
    );
}

function getInputParams({ companyName, companyLogo, companyReplyToEmail }: FormFields): BrandedSettingsSaveParams {
    return {
        // After form validations, these fields can only end up here as this types.
        companyName: companyName as string,
        companyLogo,
        companyReplyToEmail,
    };
}

export function getValidationSchema(t: TFunction) {
    return yup.object<FormFields>({
        companyName: yup
            .string()
            .optional()
            .max(250, t('form.error.max', { field: t('form.label.company-name'), number: 250 })),
        companyReplyToEmail: yup
            .string()
            .optional()
            .email(t('form.error.format', { field: t('form.label.company-email') }))
            .max(50, t('form.error.max', { field: t('form.label.company-email'), number: 50 })),
        companyLogo: yup
            .mixed<File | string>()
            .optional()
            .test(
                'fileSize',
                t('branded-emails.email.file-size-error'),
                (value?: File | string | null): value is File => {
                    // If it's an url we don't need to validate
                    return typeof value === 'string' || (value ? value?.size <= MAX_BYTE_FILE_SIZE : true);
                },
            )
            .test(
                'fileInvalidType',
                t('branded-emails.email.file-type-error'),
                (value?: File | string | null): value is File => {
                    // If it's an url we don't need to validate
                    return typeof value === 'string' || (value ? SUPPORTED_FORMATS.includes(value.type) : true);
                },
            ),
    });
}
